import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { createApi, type components } from '@awj/api-client';

import { SESSION_EXPIRED, client } from './api.ts';

type Profile = components['schemas']['UserProfileResponse'];

/** Either role can reach the admin; only SuperAdmin sees the parts marked for it. */
export const STAFF_ROLES = ['Admin', 'SuperAdmin'] as const;

export type StaffRole = (typeof STAFF_ROLES)[number];

interface SessionContextValue {
  profile: Profile | null;
  /** Null while the first answer is outstanding. */
  signedIn: boolean | null;
  /** Signed in, but without a staff role — a customer who found the URL. */
  isStaff: boolean;
  roles: string[];
  has: (role: StaffRole) => boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

/**
 * Who is using the admin.
 *
 * Asked outright on every load, with no hint-cookie shortcut. The storefront can afford to guess
 * because most of its visitors are anonymous and a wrong guess costs one wrong word in a header;
 * here every page needs a real answer before it can render anything, and there is one page load
 * per shift rather than one per product view.
 *
 * The guard this feeds is presentation only. Every endpoint behind it is authorised by the API,
 * so the worst a bypass achieves is an empty screen full of 403s.
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [signedIn, setSignedIn] = useState<boolean | null>(null);

  const refresh = useCallback(async () => {
    try {
      // Allowed one token refresh. The access cookie lasts ten minutes and the refresh cookie for
      // days, so skipping the refresh meant reloading the panel after a short break showed the
      // sign-in screen — while the same person, had they clicked anything instead of reloading,
      // would have been refreshed silently. A 401 that survives the refresh is the answer.
      const found = await createApi(client()).account.me();

      setProfile(found);
      setSignedIn(true);
    } catch {
      setProfile(null);
      setSignedIn(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    const forget = () => {
      setProfile(null);
      setSignedIn(false);
      // Everything cached was read as that person. Dropping it stops a stale order list showing
      // through the sign-in screen, and stops it reappearing for whoever signs in next.
      queryClient.clear();
    };

    window.addEventListener(SESSION_EXPIRED, forget);

    return () => window.removeEventListener(SESSION_EXPIRED, forget);
  }, [queryClient]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      const api = createApi(client());

      await api.auth.login({ email, password });

      // Read the profile rather than trusting the login response: roles decide what renders,
      // and `me` is the endpoint that reports them.
      setProfile(await api.account.me());
      setSignedIn(true);
    },
    [],
  );

  const signOut = useCallback(async () => {
    try {
      await createApi(client()).auth.logout();
    } finally {
      setProfile(null);
      setSignedIn(false);
      queryClient.clear();
    }
  }, [queryClient]);

  const value = useMemo<SessionContextValue>(() => {
    const roles = profile?.roles ?? [];

    return {
      profile,
      signedIn,
      roles,
      isStaff: roles.some((role) => STAFF_ROLES.includes(role as StaffRole)),
      has: (role) => roles.includes(role),
      signIn,
      signOut,
      refresh,
    };
  }, [profile, signedIn, signIn, signOut, refresh]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const context = useContext(SessionContext);

  if (!context) {
    throw new Error('useSession must be used inside a <SessionProvider>.');
  }

  return context;
}
